import * as React from 'react';
import { Redirect } from 'react-router';
import { Formik, FormikConfig } from 'formik';
import * as A from 'fp-ts/Array';
import * as O from 'fp-ts/Option';

import { pipe } from 'fp-ts/lib/function';
import {
  FullMatchFragment,
  KitItem,
  useSetKitMutation,
  useTeamKitItemsQuery,
} from '../../generated/graphql';
import { Heading } from '../../components/Heading';

import { ChecklistItem } from '../../components/Checklist';

import { SubmitButton } from '../../components/SubmitButton';
import { useAlert } from '../../contexts/AlertContext';
import { ButtonLink } from '../../components/Button';
import { formatErrors } from '../../utils/formatErrors';

import { getSelectedKeys } from '../../utils/getSelectedKeys';
import { reduceToSelections } from '../../utils/reduceToSelections';
import { KitIcon } from '../../components/KitIcon';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { TextLink } from '../../components/ClickableText';
import { Loader } from '../../components/LoadingContainer';
import { paths } from '../../paths';
import { isNil } from '../../utils/isNil';
import { getKitIconBackgroundStyle } from './utils';

interface Props {
  match: FullMatchFragment;
}

type Fields = Record<string, boolean>;

interface PickSquadFormProps {
  match: Pick<FullMatchFragment, 'id' | 'team' | 'kitItems'>;

  availableKits: Pick<KitItem, 'id' | 'name'>[];

  onSubmit: FormikConfig<Fields>['onSubmit'];
  loading: boolean;
}

const NoKitMessage: React.FC = () => {
  return (
    <div className="space-y-4">
      <p className="text-center">
        You can't select kit for this match as you don't have any yet.
      </p>
      <p className="text-center">
        Head over to{' '}
        <TextLink to={paths.teamSettings.kit.home()}>team settings</TextLink> to
        add some kit now.
      </p>
    </div>
  );
};

const PickKitForm: React.FC<PickSquadFormProps> = ({
  loading,
  match,
  availableKits,
  onSubmit,
}) => {
  const selectedKitItems = match.kitItems.reduce<Fields>(
    reduceToSelections(true),
    {},
  );
  const allKitItems = availableKits.reduce<Fields>(
    reduceToSelections(false),
    {},
  );

  const initialValues = { ...allKitItems, ...selectedKitItems };
  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ handleChange, values, handleSubmit }) => {
        const selectedKitIds = getSelectedKeys(values);

        return (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex justify-between">
              <Heading level={3}>
                {selectedKitIds.length} item
                {selectedKitIds.length === 1 ? '' : 's'} selected
              </Heading>
              <TextLink to={paths.teamSettings.kit.home()}>
                Manage team kits
              </TextLink>
            </div>
            <ul>
              {availableKits.map((kitItem) => (
                <li key={kitItem.id}>
                  <ChecklistItem
                    name={kitItem.id}
                    onChange={handleChange}
                    checked={values[kitItem.id]}
                    value={kitItem.id}
                  >
                    <KitIcon
                      className="mr-2"
                      size="md"
                      color={getKitIconBackgroundStyle(kitItem.name)}
                    ></KitIcon>
                    <p>{kitItem.name}</p>
                  </ChecklistItem>
                </li>
              ))}
            </ul>

            <SubmitButton loading={loading}>Confirm Kit</SubmitButton>
            <ButtonLink to={`/m/${match.id}`} kind="plain" icon="x">
              Cancel
            </ButtonLink>
          </form>
        );
      }}
    </Formik>
  );
};

export const PickKit: React.FC<Props> = ({ match }) => {
  const analytics = useAnalytics();
  const [kitItemsResponse] = useTeamKitItemsQuery({
    variables: { id: match.team.id },
    requestPolicy: 'cache-and-network',
  });

  const [setKitResponse, setKit] = useSetKitMutation();
  const alert = useAlert();

  if (kitItemsResponse.fetching) {
    return <Loader />;
  }

  const submitSetKit: FormikConfig<Fields>['onSubmit'] = (values, actions) => {
    analytics.track('Form Submitted', { form: 'pick_kit' });
    setKit({
      input: {
        matchId: match.id,
        kitItemIds: getSelectedKeys(values),
      },
    }).then((res) => {
      pipe(
        res.data?.setKit?.validation,
        O.fromNullable,
        O.map(formatErrors),
        O.fold(() => {
          analytics.track('Data Submit Succeeded', {
            form: 'pick_kit',
          });
          alert('Kit confirmed');
        }, actions.setErrors),
      );
    });
  };

  if (
    setKitResponse.data?.setKit?.entity &&
    isNil(setKitResponse.data.setKit.validation)
  ) {
    return <Redirect to={paths.match.home(match.id)} />;
  }

  const availableKits = kitItemsResponse.data?.team?.kitItems;

  return isNil(availableKits) || A.isEmpty(availableKits) ? (
    <NoKitMessage />
  ) : (
    <PickKitForm
      loading={setKitResponse.fetching}
      match={match}
      availableKits={availableKits}
      onSubmit={submitSetKit}
    />
  );
};
